import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useAuth } from "./stores/auth";
import { useQueue } from "./stores/queue";
import { usePrefs } from "./stores/prefs";
import { Login } from "./routes/Login";
import { Home } from "./routes/Home";
import { StockIn } from "./routes/StockIn";
import { StockOut } from "./routes/StockOut";
import { TransferOut } from "./routes/TransferOut";
import { TransferIn } from "./routes/TransferIn";
import { Inventario } from "./routes/Inventario";
import { Events } from "./routes/Events";
import { Settings as SettingsPage } from "./routes/Settings";
import { Debug } from "./routes/Debug";
import { BottomNav } from "./components/ui/BottomNav";
import { TopBarSlotContext } from "./components/ui/TopBar";

function Protected({ children }: { children: React.ReactNode }) {
  const access = useAuth((s) => s.access);
  const location = useLocation();
  if (!access) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}

function Shell({ children }: { children: React.ReactNode }) {
  // A ref-callback into state: once the host DOM node attaches, this re-renders
  // and the context hands the node to any descendant TopBar to portal into.
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);

  // The bars are position:fixed, so content must reserve exactly their rendered
  // height (safe-area included). Measure the real DOM and publish it as CSS vars
  // — one source of truth for the scroll container's padding.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const measure = () => {
      if (slot) root.style.setProperty("--topbar-h", `${slot.offsetHeight}px`);
      if (navRef.current) root.style.setProperty("--nav-h", `${navRef.current.offsetHeight}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (slot) ro.observe(slot);
    if (navRef.current) ro.observe(navRef.current);
    return () => ro.disconnect();
  }, [slot]);

  // iOS keeps position:fixed bars pinned to the *layout* viewport, so when the
  // soft keyboard opens the bottom nav floats above it. Detect the shrink via
  // visualViewport and hide the nav while typing.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    const onResize = () => {
      const keyboardOpen = window.innerHeight - vv.height > 150;
      root.classList.toggle("keyboard-open", keyboardOpen);
    };
    vv.addEventListener("resize", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      root.classList.remove("keyboard-open");
    };
  }, []);

  return (
    <div className="tcg-app">
      <div className="tcg-topbar-host" ref={setSlot} />
      <TopBarSlotContext.Provider value={slot}>
        <div className="tcg-scroll">{children}</div>
      </TopBarSlotContext.Provider>
      <BottomNav ref={navRef} />
    </div>
  );
}

export default function App() {
  const access = useAuth((s) => s.access);
  const flush = useQueue((s) => s.flush);
  const hydratePrefs = usePrefs((s) => s.hydrateFromServer);

  useEffect(() => {
    if (navigator.onLine) void flush();
  }, [flush]);

  useEffect(() => {
    if (access) void hydratePrefs();
  }, [access, hydratePrefs]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/debug" element={<Shell><Debug /></Shell>} />
      <Route path="/" element={<Protected><Shell><Home /></Shell></Protected>} />
      <Route path="/stock-in" element={<Protected><Shell><StockIn /></Shell></Protected>} />
      <Route path="/stock-out" element={<Protected><Shell><StockOut /></Shell></Protected>} />
      <Route path="/transfer-out" element={<Protected><Shell><TransferOut /></Shell></Protected>} />
      <Route path="/transfer-in" element={<Protected><Shell><TransferIn /></Shell></Protected>} />
      <Route path="/inventario" element={<Protected><Shell><Inventario /></Shell></Protected>} />
      <Route path="/events" element={<Protected><Shell><Events /></Shell></Protected>} />
      <Route path="/settings" element={<Protected><Shell><SettingsPage /></Shell></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
